import {
  readContract,
  writeContract,
  waitForTransactionReceipt,
  getPublicClient,
} from '@wagmi/core'
import { decodeEventLog } from 'viem'
import type { Config } from '@wagmi/core'
import { getBalance } from 'viem/actions'
import { DEADMAN_SWITCH_ABI } from './abi.ts'
import { ERC20_ABI } from './erc20.abi.ts'
import { contractConfig } from './contract.config.ts'
import { wagmiAdapter } from '../wallet/reown.config.ts'
import type { DeadmanSwitchService } from './contract.interface.ts'
import type {
  Subscription,
  Vault,
  VaultDecryptionInfo,
  PricingInfo,
  TokenInfo,
  CreateVaultParams,
  ContractWriteResult,
} from './contract.types.ts'
import type { SubscriptionTier } from './contract.types.ts'

function getConfig(): Config {
  return wagmiAdapter.wagmiConfig as Config
}

const addr = contractConfig.address
const chainId = contractConfig.chainId

async function writeTx(hash: `0x${string}`): Promise<ContractWriteResult> {
  await waitForTransactionReceipt(getConfig(), { hash })
  return { hash }
}

interface RawSubscription {
  startTime: bigint
  expiryTime: bigint
  tier: number
  exists: boolean
}

interface RawVault {
  id: bigint
  benefactor: `0x${string}`
  beneficiary: `0x${string}`
  cid: string
  encryptionHash: `0x${string}`
  countdownDuration: bigint
  countdownStartTime: bigint
  lastResetTime: bigint
  isCountdownActive: boolean
  exists: boolean
}

function parseSubscription(raw: RawSubscription): Subscription {
  return {
    startTime: raw.startTime,
    expiryTime: raw.expiryTime,
    tier: raw.tier as SubscriptionTier,
    exists: raw.exists,
  }
}

function parseVault(raw: RawVault): Vault {
  return {
    id: raw.id,
    benefactor: raw.benefactor,
    beneficiary: raw.beneficiary,
    cid: raw.cid,
    encryptionHash: raw.encryptionHash,
    countdownDuration: raw.countdownDuration,
    countdownStartTime: raw.countdownStartTime,
    lastResetTime: raw.lastResetTime,
    isCountdownActive: raw.isCountdownActive,
    exists: raw.exists,
  }
}

export function createDeadmanSwitchService(): DeadmanSwitchService {
  return {
    // --- Read: Subscription ---
    async getSubscription(userAddress) {
      const raw = await readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getSubscription',
        args: [userAddress],
      })
      return parseSubscription(raw as unknown as RawSubscription)
    },

    async isSubscriptionActive(userAddress) {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'isSubscriptionActive',
        args: [userAddress],
      }) as Promise<boolean>
    },

    async getPricing() {
      const [oneMonthPrice, oneYearPrice] = await Promise.all([
        readContract(getConfig(), {
          address: addr,
          abi: DEADMAN_SWITCH_ABI,
          functionName: 'oneMonthPrice',
        }) as Promise<bigint>,
        readContract(getConfig(), {
          address: addr,
          abi: DEADMAN_SWITCH_ABI,
          functionName: 'oneYearPrice',
        }) as Promise<bigint>,
      ])
      return {
        oneMonthPrice,
        oneYearPrice,
      } satisfies PricingInfo
    },

    // --- Read: Vault ---
    async getMyVaults(account?: `0x${string}`) {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getMyVaults',
        ...(account && { account }),
      }) as Promise<bigint[]>
    },

    async getVaultDetails(vaultId, account?: `0x${string}`) {
      const raw = await readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getVaultDetails',
        args: [vaultId],
        ...(account && { account }),
      })
      return parseVault(raw as unknown as RawVault)
    },

    async getTimeRemaining(vaultId) {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getTimeRemaining',
        args: [vaultId],
      }) as Promise<bigint>
    },

    async getTotalVaults() {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getTotalVaults',
      }) as Promise<bigint>
    },

    // --- Read: Beneficiary ---
    async getVaultsAssignedToMe(account?: `0x${string}`) {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getVaultsAssignedToMe',
        ...(account && { account }),
      }) as Promise<bigint[]>
    },

    async canDecrypt(beneficiary, vaultId) {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'canDecrypt',
        args: [beneficiary, vaultId],
      }) as Promise<boolean>
    },

    async getVaultDecryptionInfo(vaultId) {
      const result = await readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getVaultDecryptionInfo',
        args: [vaultId],
      })
      const [cid, encryptionHash] = result as readonly [string, `0x${string}`]
      return { cid, encryptionHash } satisfies VaultDecryptionInfo
    },

    // --- Read: Token ---
    async getPaymentToken() {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getPaymentToken',
      }) as Promise<`0x${string}`>
    },

    async getTokenInfo() {
      const tokenAddress = await this.getPaymentToken()

      const [symbol, name, decimals] = await Promise.all([
        readContract(getConfig(), {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'symbol',
        }) as Promise<string>,
        readContract(getConfig(), {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'name',
        }) as Promise<string>,
        readContract(getConfig(), {
          address: tokenAddress,
          abi: ERC20_ABI,
          functionName: 'decimals',
        }) as Promise<number>,
      ])

      return {
        address: tokenAddress,
        symbol,
        name,
        decimals,
      } satisfies TokenInfo
    },

    async getTokenBalance(userAddress) {
      const tokenAddress = await this.getPaymentToken()
      return readContract(getConfig(), {
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [userAddress],
      }) as Promise<bigint>
    },

    async getTokenAllowance(owner, spender) {
      const tokenAddress = await this.getPaymentToken()
      return readContract(getConfig(), {
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [owner, spender],
      }) as Promise<bigint>
    },

    // --- Read: Contract state ---
    async isPaused() {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'paused',
      }) as Promise<boolean>
    },

    async getCooldownPeriod() {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'cooldownPeriod',
      }) as Promise<bigint>
    },

    async getOwner() {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'owner',
        chainId,
      }) as Promise<`0x${string}`>
    },

    async getContractTokenBalance() {
      return this.getTokenBalance(addr)
    },

    async getVaultCooldownPeriod(vaultId) {
      return readContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'getVaultCooldownPeriod',
        args: [vaultId],
      }) as Promise<bigint>
    },

    async getContractEthBalance() {
      const publicClient = getPublicClient(getConfig(), { chainId })
      if (!publicClient) return 0n
      return getBalance(publicClient, { address: addr })
    },

    // --- Write: Subscription ---
    async subscribe(tier) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'subscribe',
        args: [tier],
      })
      return writeTx(hash)
    },

    async renewSubscription(tier) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'renewSubscription',
        args: [tier],
      })
      return writeTx(hash)
    },

    // --- Write: Vault ---
    async createVault(params: CreateVaultParams) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'createVault',
        args: [
          params.cid,
          params.encryptionHash,
          params.beneficiary,
          params.countdownMinutes,
        ],
      })
      const receipt = await waitForTransactionReceipt(getConfig(), { hash })
      const contractLower = addr.toLowerCase()
      for (const log of receipt.logs) {
        if (log.address.toLowerCase() !== contractLower) continue
        try {
          const decoded = decodeEventLog({
            abi: DEADMAN_SWITCH_ABI,
            data: log.data,
            topics: log.topics,
          })
          if (
            decoded.eventName === 'VaultCreated' &&
            typeof decoded.args === 'object' &&
            decoded.args !== null &&
            'vaultId' in decoded.args
          ) {
            return {
              hash,
              vaultId: decoded.args.vaultId as bigint,
            }
          }
        } catch {
          // not a VaultCreated log
        }
      }
      return { hash }
    },

    async resetCountdown(vaultId) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'resetCountdown',
        args: [vaultId],
      })
      return writeTx(hash)
    },

    async startCountdown(vaultId) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'startCountdown',
        args: [vaultId],
      })
      return writeTx(hash)
    },

    async updateBeneficiary(vaultId, newBeneficiary) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'updateBeneficiary',
        args: [vaultId, newBeneficiary],
      })
      return writeTx(hash)
    },

    // --- Write: Beneficiary ---
    async accessVault(vaultId) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'accessVault',
        args: [vaultId],
      })
      return writeTx(hash)
    },

    // --- Write: Token ---
    async approveToken(spender, amount) {
      const tokenAddress = await this.getPaymentToken()
      const hash = await writeContract(getConfig(), {
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [spender, amount],
      })
      return writeTx(hash)
    },

    // --- Write: Admin (owner only) ---
    async withdrawFees() {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'withdrawFees',
      })
      return writeTx(hash)
    },

    async setOneMonthPrice(price) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'setOneMonthPrice',
        args: [price],
      })
      return writeTx(hash)
    },

    async setOneYearPrice(price) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'setOneYearPrice',
        args: [price],
      })
      return writeTx(hash)
    },

    async setVaultCooldownPeriod(vaultId, newCooldownPeriod) {
      const hash = await writeContract(getConfig(), {
        address: addr,
        abi: DEADMAN_SWITCH_ABI,
        functionName: 'setVaultCooldownPeriod',
        args: [vaultId, newCooldownPeriod],
      })
      return writeTx(hash)
    },
  }
}
